import { spaceUsersListUrl } from "@Services/dataManager";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useRef, useMemo } from "react";

import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
// Columns are built with SlickGrid1's own `typedColumns` — the older `SlickGrid` module exports a
// same-named helper whose validation brand is a distinct type, which is what the grid rejects.
import { typedColumns, type GridRowBase, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import { useTranslation } from "@i18n";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * Row type for space user data. `userId` is required because the `getSpaceUsersList` aggregation
 * always projects it (`userId: "$users._id"`).
 */
export interface SpaceUserRow extends GridRowBase {
  userName?: string;
  fullName?: string;
  organisationName?: string;
  userId: ObjectIdString;
}

interface UsersListInSpaceProps {
  /** Plain `string`: callers hand this down from route params / component props, unvalidated. */
  spaceId?: string;
  selectUsers: (users: SpaceUserRow[]) => void;
  disabledUserIds?: string[];
  /**
   * DEAD PROP: passed by `AddUserModal` but read by neither this component nor the grid it renders.
   * Declared so the call site keeps type-checking; dropping it from the caller would be a
   * behaviour-neutral cleanup.
   */
  allowMultiSelection?: boolean;
}

/**
 * First selected grid row index out of an `onSelectedRowIdsChanged` event.
 *
 * The grid declares that callback's argument as an opaque index-signature record but dispatches a
 * `CustomEvent` into it (see the KNOWN MISMATCH note in SlickGrid1/index.tsx), so the index is read
 * back through `typeof`/`in` narrowing instead of an assertion on the event.
 */
function firstSelectedRow(args: Record<string, unknown>): number | undefined {
  const detail: unknown = args.detail;
  if (typeof detail !== "object" || detail === null || !("args" in detail)) return undefined;
  const eventArgs: unknown = detail.args;
  if (typeof eventArgs !== "object" || eventArgs === null || !("rows" in eventArgs)) return undefined;
  const rows: unknown = eventArgs.rows;
  if (!Array.isArray(rows)) return undefined;
  // Annotated `unknown`: `Array.isArray` narrows an `unknown` to `any[]`, which would let the
  // element escape untyped.
  const first: unknown = rows[0];
  return typeof first === "number" ? first : undefined;
}

function UsersListInSpace({ spaceId, selectUsers, disabledUserIds = [] }: UsersListInSpaceProps) {
  const dataGrid = useRef<SlickGrid1Ref<SpaceUserRow> | null>(null);
  const { t } = useTranslation();

  const spaceUsersQuery = useMemo(
    () => ({
      spaceId,
      params: {
        spaceId,
      },
    }),
    [spaceId],
  );
  const selectionEvents = (args: Record<string, unknown>) => {
    const row = firstSelectedRow(args);
    if (row === undefined) return;
    // KNOWN BUG, preserved verbatim: `dataGrid.current` is dereferenced without a guard, so an
    // event delivered before/after mount would throw.
    const rowData = dataGrid.current!.slickGrid.getDataItem(row);
    if (rowData) {
      if (disabledUserIds.includes(rowData.userId)) {
        args.cancel = true;
        return;
      }

      const selectedRecords = dataGrid.current!.dataView.getAllSelectedItems();
      selectUsers(selectedRecords);
    }
  };

  const userNameTemplate = (
    _row: number,
    _cell: number,
    _value: string | undefined,
    _columnDef: unknown,
    dataContext: SpaceUserRow,
  ) => {
    const container = document.createElement("div");
    const { userId } = dataContext;

    renderUserTag(container, { id: userId });
    return container;
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<SpaceUserRow>([
        {
          field: "userName",
          id: "fullName",
          name: t("Users"),
          _valueGetter: (params) => params.fullName,
          formatter: userNameTemplate,
          filterType: "Text",
        },
        {
          field: "organisationName",
          id: "organisation",
          name: t("Organization"),
          filterType: "Text",
          _valueGetter: (params) => params.organisationName,
        },
      ]),
    [],
  );

  if (!spaceId) {
    return null;
  }

  return (
    <div className="h-[18.75rem]">
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={spaceUsersListUrl}
        query={spaceUsersQuery}
        columnDefinitions={columnDefinitions}
        disabledIds={disabledUserIds}
        enableFilteringOption
        onSelectedRowIdsChanged={selectionEvents}
      />
    </div>
  );
}

export default UsersListInSpace;
